// StatusBadge — one status→colour mapping for the whole console.
//
// Every domain in this app has a status enum (invoice, subscription, refund,
// email, moderation, plan, page) and the old admin coloured them ad hoc, so
// `paid` was green on one screen and neutral on another. Staff learn colour
// faster than text; it has to mean the same thing everywhere.
//
// The semantic tiers:
//   success  — terminal and good (paid, active, succeeded, published)
//   warning  — needs attention, still recoverable (past_due, pending, draft)
//   danger   — terminal and bad (failed, canceled, uncollectible)
//   info     — in progress, no action needed (processing, scheduled)
//   neutral  — inert or archived (void, archived, canceled-by-design)
//
// A status NOT in the map renders neutral with its raw label rather than
// throwing — a new enum value from the API must never blank a table cell.

import { cn } from '@fajrarabi/ui';

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TONE_CLASS: Record<StatusTone, string> = {
  success: 'bg-success-50 text-success-700 ring-success-500/20',
  warning: 'bg-warning-50 text-warning-700 ring-warning-500/20',
  danger: 'bg-error-50 text-error-700 ring-error-500/20',
  info: 'bg-brand-50 text-brand-700 ring-brand-500/20',
  neutral: 'bg-admin-100 text-admin-600 ring-admin-300/40',
};

// Keyed by the raw DB enum value. Grouped by domain for readability; the
// lookup is flat because these values do not collide across domains except
// where they genuinely mean the same thing (`canceled`, `active`).
const STATUS_TONE: Record<string, StatusTone> = {
  // invoices
  draft: 'neutral',
  open: 'warning',
  paid: 'success',
  void: 'neutral',
  uncollectible: 'danger',
  refunded: 'neutral',
  partially_refunded: 'warning',

  // subscriptions
  incomplete: 'warning',
  trialing: 'info',
  active: 'success',
  past_due: 'danger',
  canceled: 'neutral',
  expired: 'neutral',
  paused: 'neutral',

  // refunds + payment events
  pending: 'warning',
  succeeded: 'success',
  failed: 'danger',
  processing: 'info',

  // email
  queued: 'info',
  sent: 'success',
  delivered: 'success',
  bounced: 'danger',
  complained: 'danger',
  suppressed: 'neutral',

  // content / publishing
  published: 'success',
  scheduled: 'info',
  archived: 'neutral',
  hidden: 'neutral',

  // moderation
  approved: 'success',
  rejected: 'danger',
  flagged: 'warning',
};

/** Human label for a snake_case enum value: `partially_refunded` → `Partially refunded`. */
export function humanizeStatus(status: string): string {
  const spaced = status.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function statusTone(status: string): StatusTone {
  return STATUS_TONE[status.toLowerCase()] ?? 'neutral';
}

export interface StatusBadgeProps {
  status: string;
  /** Override the derived label (e.g. to add a count). */
  label?: string;
  /** Force a tone — only for statuses whose meaning is context-dependent. */
  tone?: StatusTone;
  className?: string;
  /** Adds a filled dot; helps when many badges sit in one column. */
  dot?: boolean;
}

export function StatusBadge({ status, label, tone, className, dot = false }: StatusBadgeProps) {
  const resolved = tone ?? statusTone(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONE_CLASS[resolved],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />}
      {label ?? humanizeStatus(status)}
    </span>
  );
}
